import React, { useState } from "react";
import Sheet from "../../components/layout/app/Sheet";
import { DPAD, KEYS, NUMBER_KEYS, NUMBER_PAD } from "./layouts";

// Remote keys drawn like the app's RoundButton, VerticalButton and
// DirectionButton (app/screens/HashhRemote/Layouts). Keys whose code isn't
// learned are disabled.

// Every press replays a short pulse. The class alternates between two names so
// quick repeated presses restart the animation.
const KeyButton = ({ className, label, disabled, onPress, children, ...rest }) => {
  const [pulse, setPulse] = useState(0);

  const press = () => {
    setPulse((value) => (value === 1 ? 2 : 1));
    onPress();
  };

  return (
    <button
      type="button"
      className={pulse ? `${className} is-pulse-${pulse}` : className}
      aria-label={label}
      disabled={disabled}
      onClick={press}
      onAnimationEnd={() => setPulse(0)}
      {...rest}
    >
      {children}
    </button>
  );
};

// Round key with an icon or a short text.
export const RoundKey = ({ label, icon: Icon, text, tone, big = false, disabled, onPress, ...rest }) => {
  const classes = ["hx-remote-key", big ? "is-big" : "", tone ? `is-${tone}` : ""].filter(Boolean).join(" ");
  return (
    <KeyButton className={classes} label={label} disabled={disabled} onPress={onPress} {...rest}>
      {Icon ? <Icon aria-hidden="true" /> : <span aria-hidden="true">{text}</span>}
    </KeyButton>
  );
};

// Tall pill with an up and a down key and the pair's name between them.
// up and down: { label, icon, disabled, onPress }.
export const RockerKey = ({ label, groupLabel, up, down }) => {
  const UpIcon = up.icon;
  const DownIcon = down.icon;
  const off = up.disabled && down.disabled;
  return (
    <div className={`hx-remote-rocker${off ? " is-off" : ""}`} role="group" aria-label={groupLabel}>
      <KeyButton className="hx-remote-rocker-btn" label={up.label} disabled={up.disabled} onPress={up.onPress}>
        <UpIcon aria-hidden="true" />
      </KeyButton>
      <span className="hx-remote-rocker-label" aria-hidden="true">
        {label}
      </span>
      <KeyButton className="hx-remote-rocker-btn" label={down.label} disabled={down.disabled} onPress={down.onPress}>
        <DownIcon aria-hidden="true" />
      </KeyButton>
    </div>
  );
};

// Round pad cut into four quarters, with OK below it as in the app.
export const DirectionPad = ({ isLearned, onKey }) => (
  <div className="hx-remote-dpad-wrap" role="group" aria-label="Direction pad">
    <div className="hx-remote-dpad">
      <div className="hx-remote-dpad-grid">
        {DPAD.map((name) => {
          const { label, icon: Icon } = KEYS[name];
          return (
            <KeyButton
              key={name}
              className={`hx-remote-dpad-btn is-${name.toLowerCase()}`}
              label={label}
              disabled={!isLearned(name)}
              onPress={() => onKey(name)}
            >
              <span className="hx-remote-dpad-icon">
                <Icon aria-hidden="true" />
              </span>
            </KeyButton>
          );
        })}
      </div>
    </div>
    <RoundKey label="OK" text="OK" big disabled={!isLearned("OK")} onPress={() => onKey("OK")} />
  </div>
);

// Digits in a bottom sheet that stays open between presses, like the app's
// NumberPanel.
export const NumberPad = ({ open, onClose, isLearned, onKey }) => (
  <Sheet open={open} onClose={onClose} title="Numbers">
    <div className="hx-remote-numpad">
      {NUMBER_PAD.map((name, index) => {
        if (!name) return <span key={index} aria-hidden="true" />;
        const digit = name.slice("NUM_".length);
        return (
          <RoundKey
            key={name}
            label={`Number ${digit}`}
            text={digit}
            disabled={!isLearned(name)}
            onPress={() => onKey(name)}
          />
        );
      })}
    </div>
  </Sheet>
);

// A remote's rows of keys (see LAYOUTS in layouts.js).
export const KeyLayout = ({ rows, isLearned, onKey, onNumbers, numbersOpen }) => {
  const side = (name) => ({
    label: KEYS[name].label,
    icon: KEYS[name].icon,
    disabled: !isLearned(name),
    onPress: () => onKey(name),
  });

  const renderItem = (item, index) => {
    switch (item.kind) {
      case "key": {
        const info = KEYS[item.name];
        return (
          <RoundKey
            key={index}
            label={info.label}
            icon={item.icon ?? info.icon}
            tone={info.tone}
            disabled={!isLearned(item.name)}
            onPress={() => onKey(item.name)}
          />
        );
      }
      case "numbers":
        return (
          <RoundKey
            key={index}
            label="Number pad 123"
            text="123"
            disabled={!NUMBER_KEYS.some((name) => isLearned(name))}
            onPress={onNumbers}
            aria-haspopup="dialog"
            aria-expanded={numbersOpen}
          />
        );
      case "rocker":
        return (
          <RockerKey
            key={index}
            label={item.label}
            groupLabel={item.groupLabel}
            up={side(item.keys[0])}
            down={side(item.keys[1])}
          />
        );
      case "dpad":
        return <DirectionPad key={index} isLearned={isLearned} onKey={onKey} />;
      default:
        return <span key={index} className="hx-remote-spacer" aria-hidden="true" />;
    }
  };

  return (
    <>
      {rows.map((row, index) => (
        <div key={index} className="hx-remote-row">
          {row.map(renderItem)}
        </div>
      ))}
    </>
  );
};
